import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import AdminSideMenu from "../components/AdminSideMenu";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu";
import styles from "./UsersScreen.module.css"; // reuse same grid look and container
import { getRoomStats, RoomSessionItem } from "../services/roomStatsService";

ModuleRegistry.registerModules([AllCommunityModule]);

const SessionHistoryScreen: FunctionComponent = () => {
    const [rows, setRows] = useState<RoomSessionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [roomFilter, setRoomFilter] = useState("");
    const pageSize = 100; // default paging 100

    const fetchData = async (page: number, roomId?: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getRoomStats(page, pageSize, roomId);
            setRows(data.items);
            setTotalPages(data.pages ?? 0);
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message || 'Failed to load session history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(currentPage, roomFilter.trim() || undefined);
    }, [currentPage]);

    const onApplyFilter = () => {
        setCurrentPage(1);
        fetchData(1, roomFilter.trim() || undefined);
    };

    const durationCell = (startIso?: string, endIso?: string | null) => {
        if (!startIso) return '-';
        const start = new Date(startIso).getTime();
        const end = endIso ? new Date(endIso).getTime() : Date.now();
        const diffMs = Math.max(0, end - start);
        const mins = Math.floor(diffMs / 60000);
        const hrs = Math.floor(mins / 60);
        const remMins = mins % 60;
        return hrs > 0 ? `${hrs}h ${remMins}m` : `${remMins}m`;
    };

    const languagesSummary = (stats?: string | null) => {
        if (!stats) return '-';
        try {
            const parsed = JSON.parse(stats);
            const langs = parsed?.languages || {};
            const entries = Object.entries(langs) as [string, number][];
            if (entries.length === 0) return '-';
            return entries.map(([code, count]) => `${code}:${count}`).join(', ');
        } catch {
            return '-';
        }
    };

    const columnDefs = useMemo<ColDef<RoomSessionItem>[]>(() => [
        { headerName: 'Room ID', field: 'roomId', minWidth: 140 },
        { headerName: 'Room Name', field: 'roomName', flex: 1, minWidth: 180 },
        { headerName: 'Start Date', field: 'startDate', valueFormatter: p => p.value ? new Date(p.value).toLocaleString() : '-', minWidth: 200 },
        { headerName: 'Duration', valueGetter: p => durationCell(p.data?.startDate, p.data?.endDate), minWidth: 120 },
        { headerName: 'Total Listeners', field: 'listeners', minWidth: 140 },
        { headerName: 'Listeners per Language', valueGetter: p => languagesSummary(p.data?.stats), flex: 1, minWidth: 220 },
    ], []);

    const defaultColDef = useMemo(() => ({ sortable: true, resizable: true }), []);

    return (
        <div>
            <AdminSideMenu />
            <AdminTopMenu />
            <div className={styles.container}>
                <div className={styles.mainContent}>
                    <div className={styles.header}>
                        <h1 className={styles.headerTitle}>Session History</h1>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                        <input
                            type="text"
                            placeholder="Filter by Room ID..."
                            value={roomFilter}
                            onChange={(e) => setRoomFilter(e.target.value)}
                            className={styles.searchInput}
                            style={{ marginBottom: 0 }}
                        />
                        <button className={styles.addUserButton} onClick={onApplyFilter}>Apply</button>
                    </div>

                    {error && <div className={styles.errorContainer}><p>{error}</p></div>}

                    <div className={styles.usersContainer}>
                        {loading ? (
                            <div className={styles.loadingContainer}>
                                <div className={styles.loadingSpinner}></div>
                                <p>Loading sessions...</p>
                            </div>
                        ) : (
                            <div className={`ag-theme-alpine ${styles.agGridContainer}`} style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
                                <AgGridReact<RoomSessionItem>
                                    rowData={rows}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    pagination={true}
                                    paginationPageSize={pageSize}
                                    overlayNoRowsTemplate={'<span style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; border-radius: 4px; font-style: italic;">No sessions found.</span>'}
                                    rowHeight={48}
                                />
                            </div>
                        )}
                    </div>

                    {/* Simple pager mirroring Users styles */}
                    <div className={styles.pagination}>
                        <button className={styles.pageButton} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>Prev</button>
                        <span style={{ padding: '8px 12px' }}>Page {currentPage} of {Math.max(1, totalPages)}</span>
                        <button className={styles.pageButton} onClick={() => setCurrentPage(p => p + 1)} disabled={totalPages > 0 && currentPage >= totalPages}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionHistoryScreen;
